import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  TICKET_STAGES,
  ticketStage,
  type Conversation,
  type TicketStage,
} from '@supporchat/shared';
import { api } from '../lib/api';
import { Avatar } from '../components/Avatar';
import { formatPhone } from '../components/CallBar';

interface Props {
  currentUserId: string;
  canWrite: boolean;
  /** Exclusão é permissão nominal: quase todo time recebe `false` aqui. */
  canDelete: boolean;
  conversationEvent: Conversation | null;
  deletedEvent: { conversationId: string; at: number } | null;
  onOpenConversation: (conversationId: string) => void;
}

/**
 * Quadro dos tickets de suporte.
 *
 * Um ticket por cliente: o card não some quando o atendimento é encerrado — ele
 * anda até "Resolvido" e volta de lá se o cliente escrever de novo, com o mesmo
 * número e o histórico inteiro. Por isso a coluna "Resolvido" mostra só os mais
 * recentes: ela cresce para sempre.
 *
 * Arrastar não pinta o card de outra cor: executa a ação que a coluna significa
 * (assumir, devolver à fila, encerrar, reabrir). É o que mantém o quadro e a
 * caixa de entrada contando a mesma história.
 */
export function TicketsPage({
  currentUserId,
  canWrite,
  canDelete,
  conversationEvent,
  deletedEvent,
  onOpenConversation,
}: Props) {
  const [cards, setCards] = useState<Conversation[]>([]);
  /**
   * Quantos resolvidos existem ao todo.
   *
   * A única contagem que vem do servidor: os tickets ativos estão todos
   * carregados e se contam sozinhos, mas os resolvidos crescem para sempre e a
   * coluna mostra apenas os últimos.
   */
  const [resolvedTotal, setResolvedTotal] = useState(0);
  const [resolvedLimit, setResolvedLimit] = useState(0);
  const [search, setSearch] = useState('');
  const [onlyMine, setOnlyMine] = useState(false);
  const [dragging, setDragging] = useState<string | null>(null);
  const [dropTarget, setDropTarget] = useState<TicketStage | null>(null);
  const [moving, setMoving] = useState<string | null>(null);
  /**
   * Ticket cujo card está perguntando "tem certeza?".
   *
   * A confirmação mora no card, e não em um `confirm()` do navegador: o painel
   * roda dentro de um iframe do sistema principal, onde um diálogo nativo trava
   * a página inteira — e o número do ticket precisa estar à vista de quem
   * confirma, porque é ele que diz o que está sendo apagado.
   */
  const [confirmingDelete, setConfirmingDelete] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const board = await api.ticketBoard();
      setCards(board.columns.flatMap((column) => column.cards));
      setResolvedTotal(board.columns.find((column) => column.stage === 'resolvido')?.total ?? 0);
      setResolvedLimit(board.resolvedLimit);
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!conversationEvent) return;

    setCards((current) => {
      const index = current.findIndex((card) => card.id === conversationEvent.id);
      if (index < 0) return [conversationEvent, ...current];
      return current.map((card, position) => (position === index ? conversationEvent : card));
    });
  }, [conversationEvent]);

  /**
   * Tira da tela o ticket que alguém excluiu — inclusive quando fui eu, em outra
   * aba. O contador de resolvidos vem do servidor, então ele é corrigido aqui:
   * recarregar o quadro inteiro por uma exclusão seria trabalho demais para um
   * card que já se sabe que sumiu.
   */
  useEffect(() => {
    if (!deletedEvent) return;

    setCards((current) => {
      const card = current.find((item) => item.id === deletedEvent.conversationId);
      if (!card) return current;
      if (ticketStage(card) === 'resolvido') setResolvedTotal((total) => Math.max(0, total - 1));
      return current.filter((item) => item.id !== deletedEvent.conversationId);
    });
    setConfirmingDelete((current) => (current === deletedEvent.conversationId ? null : current));
  }, [deletedEvent]);

  const remove = async (conversationId: string) => {
    setDeleting(conversationId);
    setError(null);

    try {
      await api.deleteTicket(conversationId);
      // O card também sai pelo evento de tempo real, mas tirá-lo aqui é o que
      // garante a resposta imediata se o socket estiver fora do ar.
      setCards((current) => {
        const card = current.find((item) => item.id === conversationId);
        if (card && ticketStage(card) === 'resolvido') {
          setResolvedTotal((total) => Math.max(0, total - 1));
        }
        return current.filter((item) => item.id !== conversationId);
      });
      setConfirmingDelete(null);
    } catch (err) {
      setError((err as Error).message);
      // Recusa do servidor (403, ticket já apagado) tem de recolher a pergunta:
      // deixá-la aberta convida a tentar de novo o que não vai passar.
      setConfirmingDelete(null);
      await load();
    } finally {
      setDeleting(null);
    }
  };

  const move = async (conversationId: string, stage: TicketStage) => {
    const card = cards.find((item) => item.id === conversationId);
    if (!card || ticketStage(card) === stage) return;

    const from = ticketStage(card);
    setMoving(conversationId);
    setError(null);

    // Otimista: o card muda de coluna na hora e a resposta confirma. Um erro
    // recarrega o quadro inteiro, que é a única forma de ter certeza de onde o
    // ticket ficou depois de uma ação recusada.
    setCards((current) => current.map((item) => (item.id === conversationId ? { ...item, ...preview(stage) } : item)));

    // Só "Resolvido" tem contagem própria (a coluna mostra uma fatia do total);
    // as outras são contadas pelos cards, que estão todos aqui.
    if (stage === 'resolvido') setResolvedTotal((current) => current + 1);
    else if (from === 'resolvido') setResolvedTotal((current) => Math.max(0, current - 1));

    try {
      const updated = await api.moveTicket(conversationId, stage);
      setCards((current) => current.map((item) => (item.id === conversationId ? updated : item)));
    } catch (err) {
      setError((err as Error).message);
      await load();
    } finally {
      setMoving(null);
    }
  };

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return cards.filter((card) => {
      if (onlyMine && card.assignedUserId !== currentUserId) return false;
      if (!term) return true;
      const haystack = [
        card.contact.name,
        card.contact.pushName,
        card.contact.phone,
        `#${card.ticketNumber}`,
        card.lastMessagePreview,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(term);
    });
  }, [cards, search, onlyMine, currentUserId]);

  return (
    <div className="board">
      <header className="board__bar">
        <div className="search-box">
          <input
            type="search"
            placeholder="Buscar por cliente, telefone ou #número"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>

        <button className="filter-chip" aria-pressed={onlyMine} onClick={() => setOnlyMine(!onlyMine)}>
          Só os meus
        </button>

        <span className="muted board__hint">
          {canWrite
            ? 'Arraste um card para mudar o estado do atendimento.'
            : 'Seu perfil vê o quadro, mas não move os cards.'}
        </span>
      </header>

      {error && <div className="alert alert--error" style={{ margin: '0 12px 12px' }}>{error}</div>}
      {loading && <div className="empty">Carregando o quadro...</div>}

      <div className="board__columns">
        {TICKET_STAGES.map((column) => {
          const columnCards = filtered.filter((card) => ticketStage(card) === column.id);
          // O contador conta o quadro inteiro, não o que a busca deixou passar:
          // filtrar não muda quantos tickets a coluna tem.
          const loaded = cards.filter((card) => ticketStage(card) === column.id).length;
          const total = column.id === 'resolvido' ? Math.max(resolvedTotal, loaded) : loaded;
          const hidden = column.id === 'resolvido' ? Math.max(0, total - loaded) : 0;

          return (
            <section
              key={column.id}
              className="board__column"
              data-stage={column.id}
              aria-dropeffect={dragging ? 'move' : undefined}
              data-drop={dropTarget === column.id ? 'true' : undefined}
              onDragOver={(event) => {
                if (!canWrite || !dragging) return;
                event.preventDefault();
                setDropTarget(column.id);
              }}
              onDragLeave={() => setDropTarget((current) => (current === column.id ? null : current))}
              onDrop={(event) => {
                event.preventDefault();
                setDropTarget(null);
                const id = event.dataTransfer.getData('text/plain');
                if (id && canWrite) void move(id, column.id);
              }}
            >
              <header className="board__column-head">
                <span className="board__column-title">{column.label}</span>
                <span className="board__column-count">{total}</span>
              </header>
              <p className="board__column-hint">{column.hint}</p>

              <div className="board__cards">
                {columnCards.map((card) => (
                  <TicketCard
                    key={card.id}
                    card={card}
                    canWrite={canWrite}
                    canDelete={canDelete}
                    busy={moving === card.id || deleting === card.id}
                    confirmingDelete={confirmingDelete === card.id}
                    onAskDelete={() => setConfirmingDelete(card.id)}
                    onCancelDelete={() => setConfirmingDelete(null)}
                    onConfirmDelete={() => void remove(card.id)}
                    mine={card.assignedUserId === currentUserId}
                    onDragStart={(event) => {
                      event.dataTransfer.setData('text/plain', card.id);
                      event.dataTransfer.effectAllowed = 'move';
                      setDragging(card.id);
                    }}
                    onDragEnd={() => {
                      setDragging(null);
                      setDropTarget(null);
                    }}
                    onMove={(stage) => void move(card.id, stage)}
                    onOpen={() => onOpenConversation(card.id)}
                  />
                ))}

                {columnCards.length === 0 && !loading && <div className="board__empty">Vazio</div>}

                {hidden > 0 && (
                  <div className="board__more">
                    + {hidden} resolvidos mais antigos
                    <div className="field__hint">
                      A coluna mostra os {resolvedLimit} últimos; os demais estão na aba Atendimentos.
                    </div>
                  </div>
                )}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}

function TicketCard({
  card,
  canWrite,
  canDelete,
  busy,
  confirmingDelete,
  mine,
  onDragStart,
  onDragEnd,
  onMove,
  onOpen,
  onAskDelete,
  onCancelDelete,
  onConfirmDelete,
}: {
  card: Conversation;
  canWrite: boolean;
  canDelete: boolean;
  busy: boolean;
  confirmingDelete: boolean;
  mine: boolean;
  onDragStart: (event: React.DragEvent) => void;
  onDragEnd: () => void;
  onMove: (stage: TicketStage) => void;
  onOpen: () => void;
  onAskDelete: () => void;
  onCancelDelete: () => void;
  onConfirmDelete: () => void;
}) {
  const stage = ticketStage(card);
  const position = TICKET_STAGES.findIndex((item) => item.id === stage);
  const previous = TICKET_STAGES[position - 1];
  const next = TICKET_STAGES[position + 1];

  const name = card.contact.name ?? card.contact.pushName ?? card.contact.phone ?? 'Cliente';

  return (
    <article
      className={`ticket-card${busy ? ' ticket-card--busy' : ''}`}
      draggable={canWrite && !busy}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
    >
      <header className="ticket-card__head">
        <Avatar
          kind="contact"
          id={card.contact.id}
          name={name}
          version={card.contact.avatarUpdatedAt}
          size="sm"
        />
        <span className="ticket-card__number mono">#{card.ticketNumber}</span>
        {card.unreadCount > 0 && <span className="tab__badge">{card.unreadCount}</span>}
      </header>

      <button className="ticket-card__open" onClick={onOpen} title="Abrir o atendimento">
        <span className="ticket-card__name">{name}</span>
        {card.contact.phone && <span className="mono ticket-card__phone">{formatPhone(card.contact.phone)}</span>}
        <span className="ticket-card__preview">{card.lastMessagePreview ?? 'Sem mensagens'}</span>
      </button>

      <div className="ticket-card__tags">
        {card.assignedUserName && (
          <span className={`ticket-card__tag${mine ? ' ticket-card__tag--mine' : ''}`}>
            {mine ? 'você' : card.assignedUserName}
          </span>
        )}
        {card.escalationReason && (
          <span className="ticket-card__tag ticket-card__tag--reason" title={card.escalationReason}>
            {card.escalationReason}
          </span>
        )}
        {card.reopenCount > 0 && (
          <span className="ticket-card__tag" title="Vezes que o cliente voltou depois de encerrado">
            {card.reopenCount}× reaberto
          </span>
        )}
      </div>

      {/* A pergunta ocupa o lugar do rodapé em vez de aparecer acima dele: com as
          setas ainda clicáveis, um movimento do card no meio da confirmação
          deixaria a dúvida de o que foi apagado. */}
      {confirmingDelete ? (
        <footer className="ticket-card__confirm">
          <span className="ticket-card__confirm-text">
            Excluir o ticket #{card.ticketNumber} e todo o histórico dele? Não tem volta.
          </span>
          <div className="ticket-card__confirm-actions">
            <button className="btn btn--sm" disabled={busy} onClick={onCancelDelete}>
              Cancelar
            </button>
            <button className="btn btn--sm btn--danger" disabled={busy} onClick={onConfirmDelete}>
              {busy ? 'Excluindo...' : 'Excluir'}
            </button>
          </div>
        </footer>
      ) : (
        /* Arrastar não funciona no toque nem no teclado; as setas fazem o mesmo
           caminho e são o único jeito de mover um card pelo celular. */
        (canWrite || canDelete) && (
          <footer className="ticket-card__foot">
            {canWrite && (
              <button
                className="btn btn--sm"
                disabled={!previous || busy}
                title={previous ? `Mover para ${previous.label}` : undefined}
                onClick={() => previous && onMove(previous.id)}
              >
                ‹
              </button>
            )}
            <span className="muted">{formatWhen(card)}</span>
            {canDelete && (
              <button
                className="btn btn--sm btn--ghost-danger"
                disabled={busy}
                title="Excluir este ticket e todo o histórico"
                aria-label={`Excluir o ticket #${card.ticketNumber}`}
                onClick={onAskDelete}
              >
                Excluir
              </button>
            )}
            {canWrite && (
              <button
                className="btn btn--sm"
                disabled={!next || busy}
                title={next ? `Mover para ${next.label}` : undefined}
                onClick={() => next && onMove(next.id)}
              >
                ›
              </button>
            )}
          </footer>
        )
      )}
    </article>
  );
}

/**
 * Estado que o card assume enquanto o servidor não responde.
 *
 * Só o suficiente para `ticketStage` colocá-lo na coluna certa — o resto do card
 * (responsável, motivo) chega na resposta.
 */
function preview(stage: TicketStage): Partial<Conversation> {
  switch (stage) {
    case 'resolvido':
      return { status: 'closed' };
    case 'fila':
      return { status: 'pending_human', handler: 'human' };
    case 'atendimento':
      return { status: 'open', handler: 'human' };
    default:
      return { status: 'open', handler: 'bot', assignedUserId: null, assignedUserName: null };
  }
}

function formatWhen(card: Conversation): string {
  const iso = card.status === 'closed' ? (card.closedAt ?? card.lastMessageAt) : card.lastMessageAt;
  if (!iso) return 'sem mensagens';

  const date = new Date(iso);
  const sameDay = new Date().toDateString() === date.toDateString();
  return sameDay
    ? date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
    : date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
}
